import React, { useMemo } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar
} from "recharts";
import "./ParentInsightsPanel.css";

// ---- 核心防呆函數 ----
function normalizeDateString(raw) {
  if (!raw) return "";
  if (typeof raw === "string") return raw.replace("上午", "AM").replace("下午", "PM");
  if (raw instanceof Date && !isNaN(raw)) return raw.toLocaleString("zh-CN");
  return "";
}

function getSubject(toolType = "") {
  if (!toolType) return "Other";
  if (/^Math/i.test(toolType)) return "Math";
  if (/^English/i.test(toolType)) return "English";
  if (/^Science/i.test(toolType)) return "Science";
  return "Other";
}
const subjectColors = {
  Math: "#3498db",
  English: "#27ae60",
  Science: "#f1c40f",
};
const subjectNames = {
  Math: "Math",
  English: "English",
  Science: "Science",
};

function getRecentActivity(records, days = 7) {
  const now = Date.now();
  const oneDay = 24 * 3600 * 1000;
  const counts = {};
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(now - i * oneDay);
    const label = d.toLocaleDateString("zh-CN", { month: "short", day: "numeric" });
    counts[label] = 0;
  }
  records.forEach(r => {
    let label = "";
    if (typeof r.date === "string" && r.date) {
      const norm = normalizeDateString(r.date);
      const d = norm ? new Date(norm) : undefined;
      if (d && !isNaN(d)) label = d.toLocaleDateString("zh-CN", { month: "short", day: "numeric" });
      else label = r.date.slice(5, 11);
    } else if (r.date instanceof Date && !isNaN(r.date)) {
      label = r.date.toLocaleDateString("zh-CN", { month: "short", day: "numeric" });
    }
    if (label && counts[label] !== undefined) counts[label]++;
  });
  return Object.entries(counts).map(([date, count]) => ({ date, count }));
}

function getWeakness(records) {
  const wrongMap = {};
  records.forEach(r => {
    if (!r.numQuestions || r.correct === undefined) return;
    const acc = r.correct / r.numQuestions;
    if (acc < 0.8) {
      const t = r.topic || r.toolType || "";
      wrongMap[t] = (wrongMap[t] || 0) + 1;
    }
  });
  const list = Object.entries(wrongMap).sort((a, b) => b[1] - a[1]);
  if (list.length === 0) return null;
  const [topType, count] = list[0];
  return `Try more "${topType}" practices. Incorrect or below 80% accuracy ${count} times recently.`;
}

export default function ParentInsightsPanel({ records }) {
  const subjectChartData = useMemo(() => {
    if (!records || records.length === 0) return {};
    const map = { Math: [], English: [], Science: [] };
    records
      .filter(r => r.numQuestions && r.correct !== undefined && r.date)
      .forEach(r => {
        const subject = getSubject(r.toolType || r.subject || r.topic);
        if (!map[subject]) map[subject] = [];
        map[subject].push({
          date: r.date ?? "",
          accuracy: Math.round((r.correct / r.numQuestions) * 100),
          type: r.topic || r.toolType || "",
        });
      });
    Object.keys(map).forEach(sub =>
      map[sub].sort((a, b) => {
        const aS = normalizeDateString(a.date);
        const bS = normalizeDateString(b.date);
        const aD = aS ? new Date(aS) : new Date("2099/12/31");
        const bD = bS ? new Date(bS) : new Date("2099/12/31");
        return (isNaN(aD) ? 1 : aD) - (isNaN(bD) ? 1 : bD);
      })
    );
    return map;
  }, [records]);

  const hasAnyData = Object.values(subjectChartData).some(arr => arr.length > 0);
  const recentActivity = useMemo(() => getRecentActivity(records, 7), [records]);
  const weaknessMsg = useMemo(() => getWeakness(records), [records]);

  function getSummary(arr) {
    if (!arr || arr.length === 0) return null;
    const n = arr.length;
    const avg = Math.round(arr.reduce((sum, a) => sum + a.accuracy, 0) / n);
    const delta = n > 1 ? arr[n - 1].accuracy - arr[0].accuracy : 0;
    return { n, avg, delta };
  }

  if (!hasAnyData) {
    return (
      <aside className="parent-insights-panel">
        <h3 className="panel-title">Parent Insights</h3>
        <p className="placeholder-text">Nothing to show yet</p>
      </aside>
    );
  }

  return (
    <aside className="parent-insights-panel">
      <h3 className="panel-title">Parent Insights</h3>
      <div className="subject-charts-grid">
        {["English", "Math", "Science"].map(subject => {
          const data = subjectChartData[subject];
          if (!data || data.length === 0) return null;
          const summary = getSummary(data);
          let trendColor = "#444";
          let trendEmoji = "";
          if (summary) {
            if (summary.delta > 5) { trendColor = "#009e29"; trendEmoji = "▲"; }
            else if (summary.delta < -5) { trendColor = "#c90a0a"; trendEmoji = "▼"; }
            else if (summary.delta !== 0) { trendColor = "#fc9a03"; trendEmoji = "➔"; }
          }
          return (
            <div key={subject} className="subject-chart-panel">
              <h4 style={{
                color: subjectColors[subject],
                fontWeight: 700,
                margin: "6px 0 2px 0",
                letterSpacing: 1
              }}>
                {subjectNames[subject]}
                <span style={{
                  fontSize: 13,
                  fontWeight: 400,
                  marginLeft: 10,
                  color: trendColor
                }}>
                  {summary && (
                    <>
                      (平均: {summary.avg}%{" "}
                      <b style={{ color: trendColor }}>{trendEmoji}{summary.delta || ""}%</b>
                      , 總次數: {summary.n})
                    </>
                  )}
                </span>
              </h4>
              <ResponsiveContainer width="100%" height={170}>
                <LineChart data={data}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11 }}
                    minTickGap={10}
                  />
                  <YAxis domain={[0, 100]} tickFormatter={v => `${v}%`} />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload || !payload.length) return null;
                      const point = payload[0].payload;
                      const showDate = point.date
                        ? (normalizeDateString(point.date) ||
                          (typeof point.date === "string" ? point.date : (point.date instanceof Date ? point.date.toLocaleString("zh-CN") : "-")))
                        : "-";
                      const showType = (typeof point.type === "string" && point.type) ? point.type : "-";
                      const showAccuracy = point.accuracy !== undefined ? `${point.accuracy}%` : "-";
                      return (
                        <div style={{
                          background: "#fff",
                          border: "1.5px solid #dae5f7",
                          borderRadius: "11px",
                          boxShadow: "0 2px 12px #bdd5fb22",
                          padding: "12px 16px",
                          minWidth: 168
                        }}>
                          <div style={{
                            color: "#2d72c6",
                            fontWeight: 700,
                            marginBottom: 3,
                            fontSize: "1.05em"
                          }}>
                            日期：<span style={{ color: "#465" }}>{showDate}</span>
                          </div>
                          <div style={{
                            color: "#7b72c6",
                            fontWeight: 500,
                            marginBottom: 5
                          }}>
                            類型：<span style={{ color: "#298fa7" }}>{showType}</span>
                          </div>
                          <hr style={{ border: 0, borderTop: "1.5px dashed #e1e5ef", margin: "7px 0 7px 0" }} />
                          <div style={{
                            fontWeight: 800,
                            color: "#27ae60",
                            fontSize: "1.27em"
                          }}>
                            Accuracy : <span style={{ color: "#27ae60" }}>{showAccuracy}</span>
                          </div>
                        </div>
                      );
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="accuracy"
                    stroke={subjectColors[subject]}
                    dot={{ r: 2.5 }}
                    activeDot={{ r: 4.5 }}
                    strokeWidth={2}
                    name="Accuracy"
                    connectNulls
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          );
        })}
      </div>
      {/* 活躍度統計 */}
      <div className="parent-activity-hist">
        <h4 style={{ fontWeight: 600, fontSize: 15, margin: "18px 0 8px 0" }}>
          Recent 7-Day Practice Activity
        </h4>
        <ResponsiveContainer width="100%" height={66}>
          <BarChart data={recentActivity}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={28} />
            <Tooltip />
            <Bar dataKey="count" fill="#009e29" />
          </BarChart>
        </ResponsiveContainer>
      </div>
      {/* 弱點提醒與個性化建議 */}
      {weaknessMsg && (
        <div className="parent-insights-hint" style={{
          margin: "14px 0 8px 0",
          background: "#fff2e1",
          borderRadius: 8,
          padding: "8px 15px"
        }}>
          <b>Personalized Suggestion:</b> {weaknessMsg}
        </div>
      )}
      <div className="insight-hint">
        <small>
          Visualizing your child’s practice accuracy by subject.<br />
          (Each dot = a practice, Y = accuracy% | Average and ▲▼ = trend)
        </small>
      </div>
    </aside>
  );
}
